import { useCallback, useEffect, useId, useState } from 'react';
import { useNostr } from '@nostrify/react';
import { BlossomUploader } from '@nostrify/nostrify/uploaders';
import { Check, ImagePlus, Loader2, X } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useAlbumActions } from '@/hooks/useAlbumActions';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUserPictures } from '@/hooks/useUserPictures';
import { useToast } from '@/hooks/useToast';

const BLOSSOM_SERVERS = [
  'https://blossom.ditto.pub/',
  'https://blossom.dreamith.to/',
  'https://blossom.primal.net/',
];
import { type AlbumMeta } from '@/lib/album-utils';
import { getImageUrl, getCaption } from '@/lib/polaroid-utils';
import { cn } from '@/lib/utils';

interface EditAlbumDialogProps {
  album: AlbumMeta;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Tracks an individual file being uploaded. */
interface UploadingFile {
  id: string;
  file: File;
  previewUrl: string;
  status: 'uploading' | 'done' | 'error';
  eventId?: string;
  error?: string;
}

export function EditAlbumDialog({ album, open, onOpenChange }: EditAlbumDialogProps) {
  const { user } = useCurrentUser();
  const { data: userPictures, isLoading: picturesLoading } = useUserPictures(user?.pubkey, 100);
  const { nostr } = useNostr();
  const { updateAlbum } = useAlbumActions();
  const { toast } = useToast();
  const fileInputId = useId();

  const [title, setTitle] = useState(album.title);
  const [description, setDescription] = useState(album.description ?? '');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(album.photoIds));
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const isUploading = uploadingFiles.some((f) => f.status === 'uploading');
  const uploadedIds = uploadingFiles.filter((f) => f.status === 'done' && f.eventId).map((f) => f.eventId!);
  const allSelectedIds = new Set([...selectedIds, ...uploadedIds]);
  const totalSelected = allSelectedIds.size;

  // Sync state when album prop changes
  useEffect(() => {
    setTitle(album.title);
    setDescription(album.description ?? '');
    setSelectedIds(new Set(album.photoIds));
    setUploadingFiles([]);
  }, [album]);

  const togglePhoto = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** Upload a single file and publish as kind:20 — uses raw APIs for parallel safety. */
  const uploadAndPublish = useCallback(async (file: File): Promise<NostrEvent> => {
    if (!user) throw new Error('Must be logged in');

    const uploader = new BlossomUploader({
      servers: BLOSSOM_SERVERS,
      signer: user.signer,
      fetch: (input, init) => globalThis.fetch(input, {
        ...init,
        signal: AbortSignal.any([
          init?.signal ?? AbortSignal.timeout(90_000),
          AbortSignal.timeout(90_000),
        ]),
      }),
    });

    const fileTags = await uploader.upload(file);
    const url = fileTags[0]?.[1];
    if (!url) throw new Error('Upload failed: no URL returned');

    const hash = fileTags.find(([n]) => n === 'x')?.[1];
    const mimeType = fileTags.find(([n]) => n === 'm')?.[1] ?? file.type;
    const sizeVal = fileTags.find(([n]) => n === 'size')?.[1];

    const imetaEntries = ['imeta', `url ${url}`, `m ${mimeType}`];
    if (hash) imetaEntries.push(`x ${hash}`);
    if (sizeVal) imetaEntries.push(`size ${sizeVal}`);
    imetaEntries.push(`alt Photo`);

    const tags: string[][] = [imetaEntries];
    if (mimeType) tags.push(['m', mimeType]);
    if (hash) tags.push(['x', hash]);

    const event = await user.signer.signEvent({
      kind: 20,
      content: '',
      tags,
      created_at: Math.floor(Date.now() / 1000),
    });

    await nostr.event(event, { signal: AbortSignal.timeout(5000) });
    return event;
  }, [user, nostr]);

  /** Handle multiple file selection — upload each in parallel. */
  const handleFilesSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // Snapshot the FileList BEFORE clearing the input — it's a live object
    // and `e.target.value = ''` empties it in place.
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    e.target.value = '';

    const newFiles: UploadingFile[] = [];
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      const id = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      newFiles.push({ id, file, previewUrl: URL.createObjectURL(file), status: 'uploading' });
    }

    if (newFiles.length === 0) return;
    setUploadingFiles((prev) => [...prev, ...newFiles]);

    for (const uf of newFiles) {
      uploadAndPublish(uf.file)
        .then((event) => {
          setUploadingFiles((prev) =>
            prev.map((f) => f.id === uf.id ? { ...f, status: 'done' as const, eventId: event.id } : f),
          );
        })
        .catch((err) => {
          console.error('Upload failed for', uf.file.name, err);
          setUploadingFiles((prev) =>
            prev.map((f) => f.id === uf.id ? { ...f, status: 'error' as const, error: err instanceof Error ? err.message : 'Upload failed' } : f),
          );
        });
    }
  }, [uploadAndPublish]);

  const removeUploadingFile = useCallback((id: string) => {
    setUploadingFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file) URL.revokeObjectURL(file.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const handleSave = async () => {
    if (!title.trim() || !user) return;

    setIsSaving(true);
    try {
      const photoIds = [...allSelectedIds];
      const firstSelectedId = photoIds[0];
      const coverEvent = userPictures?.find((e) => e.id === firstSelectedId);
      const coverImage = coverEvent ? getImageUrl(coverEvent) : album.image;

      await updateAlbum({
        dTag: album.dTag,
        title: title.trim(),
        description: description.trim() || undefined,
        image: coverImage,
        photoIds,
        themeCoordinate: album.themeCoordinate,
      });

      // Clean up preview URLs
      for (const uf of uploadingFiles) {
        URL.revokeObjectURL(uf.previewUrl);
      }

      toast({ title: 'Album updated!' });
      onOpenChange(false);
    } catch (error) {
      console.error('Update album failed:', error);
      toast({ title: 'Failed to update album', description: 'Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  const RULED_LINES = 'repeating-linear-gradient(to bottom, transparent, transparent 27px, #C8D8E8 27px, #C8D8E8 28px)';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md p-0 gap-0 bg-polaroid-white max-h-[85vh] overflow-hidden"
        onPointerDownOutside={(e) => { if (isUploading) e.preventDefault(); }}
      >
        <div className="flex flex-col max-h-[85vh]">
        {/* Sticky header with title + save button */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 flex-shrink-0 border-b border-cream-dark/30">
          <DialogHeader className="p-0">
            <DialogTitle className="font-hand text-2xl text-pol-text">Edit Album</DialogTitle>
          </DialogHeader>
          <button
            onClick={handleSave}
            disabled={!title.trim() || isSaving || isUploading}
            className={cn(
              'font-hand text-base font-bold px-4 py-1.5 bg-pol-pink text-white transition-all active:scale-95',
              'disabled:opacity-40 disabled:cursor-not-allowed',
            )}
          >
            {isSaving ? 'Saving...' : isUploading ? 'Uploading...' : `Save${totalSelected > 0 ? ` (${totalSelected})` : ''}`}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 px-5 pb-5 pt-4 space-y-4">
            {/* Title input */}
            <div>
              <label className="text-[13px] font-semibold uppercase tracking-wide text-pol-text mb-1.5 block">
                Album Name
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Summer 2025..."
                className="w-full bg-white font-hand text-lg text-pol-text py-2.5 px-3 outline-none border border-cream-dark focus:border-pol-pink/50 transition-colors placeholder:text-pol-text-secondary/40"
                style={{ backgroundImage: RULED_LINES, backgroundPosition: '0 8px' }}
              />
            </div>

            {/* Description input */}
            <div>
              <label className="text-[13px] font-semibold uppercase tracking-wide text-pol-text mb-1.5 flex items-center gap-1.5">
                Description
                <span className="text-pol-text-secondary/50 font-normal normal-case tracking-normal text-[11px]">
                  (optional)
                </span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's this album about..."
                rows={2}
                className="w-full bg-white font-hand text-base text-pol-text py-2.5 px-3 outline-none resize-none border border-cream-dark focus:border-pol-pink/50 transition-colors placeholder:text-pol-text-secondary/40"
                style={{ backgroundImage: RULED_LINES, backgroundPosition: '0 8px' }}
              />
            </div>

            {/* Upload new photos — native <label> triggers the hidden input
                without programmatic .click(), avoiding Radix Dialog issues. */}
            <div>
              <input
                id={fileInputId}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFilesSelected}
                className="sr-only"
              />
              <label
                htmlFor={fileInputId}
                className={cn(
                  'w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-pol-pink/30 bg-pol-pink/[0.03] transition-all cursor-pointer',
                  'hover:border-pol-pink/50 hover:bg-pol-pink/[0.06] active:scale-[0.98]',
                  isUploading && 'opacity-50 pointer-events-none',
                )}
              >
                <ImagePlus size={20} className="text-pol-pink" strokeWidth={1.5} />
                <span className="font-hand text-base text-pol-text">
                  Upload New Photos
                </span>
              </label>
            </div>

            {/* Uploading files progress */}
            {uploadingFiles.length > 0 && (
              <div>
                <label className="text-[13px] font-semibold uppercase tracking-wide text-pol-text mb-1.5 block">
                  New Uploads
                  {isUploading && (
                    <span className="text-pol-pink font-normal normal-case tracking-normal text-[11px] ml-2">
                      uploading...
                    </span>
                  )}
                </label>
                <div className="grid grid-cols-3 gap-1.5">
                  {uploadingFiles.map((uf) => (
                    <div key={uf.id} className="relative aspect-square overflow-hidden">
                      <img
                        src={uf.previewUrl}
                        alt="Uploading"
                        className={cn(
                          'w-full h-full object-cover',
                          uf.status === 'uploading' && 'opacity-50',
                          uf.status === 'error' && 'opacity-30',
                        )}
                      />
                      {uf.status === 'uploading' && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Loader2 size={20} className="text-pol-pink animate-spin" />
                        </div>
                      )}
                      {uf.status === 'done' && (
                        <div className="absolute top-1 right-1 w-5 h-5 bg-pol-pink flex items-center justify-center shadow-sm">
                          <Check size={14} className="text-white" strokeWidth={3} />
                        </div>
                      )}
                      {uf.status === 'error' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <span className="font-hand text-xs text-white bg-red-500 px-1.5 py-0.5">Failed</span>
                        </div>
                      )}
                      {uf.status !== 'uploading' && (
                        <button
                          onClick={() => removeUploadingFile(uf.id)}
                          className="absolute top-1 left-1 w-4 h-4 bg-black/40 flex items-center justify-center hover:bg-black/60 transition-colors"
                          aria-label="Remove"
                        >
                          <X size={10} className="text-white" strokeWidth={3} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Existing photo picker */}
            <div>
              <label className="text-[13px] font-semibold uppercase tracking-wide text-pol-text mb-1.5 block">
                Existing Photos
                {selectedIds.size > 0 && (
                  <span className="text-pol-pink font-normal normal-case tracking-normal text-[11px] ml-2">
                    {selectedIds.size} selected
                  </span>
                )}
              </label>

              {picturesLoading ? (
                <div className="grid grid-cols-3 gap-1.5">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="aspect-square skeleton-shimmer animate-shimmer" />
                  ))}
                </div>
              ) : userPictures && userPictures.length > 0 ? (
                <div className="grid grid-cols-3 gap-1.5">
                  {userPictures.map((event) => (
                    <PhotoPickerItem
                      key={event.id}
                      event={event}
                      selected={selectedIds.has(event.id)}
                      onToggle={togglePhoto}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-4 text-center">
                  <p className="font-hand text-pol-text-secondary text-sm">No existing photos yet</p>
                </div>
              )}
            </div>

            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={!title.trim() || isSaving || isUploading}
              className={cn(
                'relative w-full bg-polaroid-white py-3.5 shadow-[0_3px_12px_rgba(45,27,78,0.12)]',
                'transition-all active:scale-[0.97] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(45,27,78,0.15)]',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[0_3px_12px_rgba(45,27,78,0.12)]',
              )}
            >
              <div
                className="absolute top-0 left-1/2 z-10 w-[80px] h-[18px] tape-rainbow"
                style={{ transform: 'translateX(-50%) translateY(-9px) rotate(1.5deg)' }}
              />
              <span className="relative z-10 font-hand text-xl font-bold text-pol-text">
                {isSaving ? 'Saving...' : isUploading ? 'Uploading...' : 'Save Changes'}
              </span>
              <div className="absolute bottom-0 left-0 right-0 h-[5px] bg-pol-pink opacity-50" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Small photo thumbnail for the picker grid */
function PhotoPickerItem({ event, selected, onToggle }: {
  event: NostrEvent;
  selected: boolean;
  onToggle: (id: string) => void;
}) {
  const imageUrl = getImageUrl(event);
  const caption = getCaption(event);

  if (!imageUrl) return null;

  return (
    <button
      onClick={() => onToggle(event.id)}
      className={cn(
        'relative aspect-square overflow-hidden transition-all',
        selected
          ? 'ring-2 ring-pol-pink ring-offset-1 ring-offset-polaroid-white scale-[0.96]'
          : 'hover:brightness-90 active:scale-95',
      )}
    >
      <img
        src={imageUrl}
        alt={caption || 'Photo'}
        className="w-full h-full object-cover"
        loading="lazy"
      />

      {selected && (
        <div className="absolute top-1 right-1 w-5 h-5 bg-pol-pink flex items-center justify-center shadow-sm">
          <Check size={14} className="text-white" strokeWidth={3} />
        </div>
      )}
    </button>
  );
}
